// SPEC-INFORME-PROPUESTA / #460 · CÓMO SE CUENTA UNA CORRIDA DEL PLANIFICADOR, leyendo lo que la
// corrida tiene guardado y sin deducir nada.
//
// POR QUÉ CAMBIÓ ESTE FICHERO (decisión técnica del 16/10/2026, opción A). Antes aquí vivía la REGLA
// que convierte bloques del solver en altas, y eso era una copia: depende del tipo de trabajo —campo
// por línea sin mirar el día, informe por `lineaId`, continuación multidía por línea y día— y con dos
// copias cada ronda de revisión ajustaba una y desajustaba la otra. El paso siguiente fue que la
// consolidación DECLARARA su plan, y tampoco bastó: un denominador que anuncia quien llama no se
// puede comprobar del todo.
//
// Ahora el plan de unidades aceptables lo PREPARA LA API, derivándolo de la propuesta que la corrida
// tiene guardada (`resolucion.planAceptacion`), y acredita las altas que de verdad se crean,
// derivando la unidad del alta ya escrita (`resolucion.unidadesAcreditadas`). Lo que una persona
// descarta a mano queda aparte (`resolucion.unidadesDescartadas`). Este módulo solo LEE esos hechos:
//
//   · denominador = unidades del plan de ESA corrida,
//   · numerador   = unidades DEL PLAN con alta persistida (unidades distintas: un reintento no suma
//                   dos veces, y un alta ajena a la corrida no está en el plan y no tapa a la que
//                   falta),
//   · descartadas = unidades del plan que alguien quitó a mano y no acabaron creándose,
//   · completa = todas con alta; resuelta = ninguna pendiente, aunque haya descartes. No es lo
//                mismo, y las dos se dicen por cobertura de identidades, nunca comparando longitudes.
//
// Y las corridas que no tienen plan conservan su interpretación histórica, identificada como tal:
// `base` dice con qué se está contando y quien pinta lo enseña en vez de disimularlo.
//
// SOLO recuento y presentación: nunca recalcula la propuesta ni inventa altas.

import UnidadAceptable from './UnidadAceptable.js';

/** La corrida trae el plan de unidades que derivó la API: se cuenta por unidades aceptables. */
const BASE_PLAN = 'PLAN_UNIDADES_V2';
/** Hay unidades acreditadas pero la corrida no tiene plan: hay numerador y no hay denominador. */
const BASE_SIN_PLAN = 'SIN_PLAN';
/** Corrida anterior a la trazabilidad de unidades: se cuenta por bloques, como se contaba entonces. */
const BASE_LEGADO = 'LEGADO_BLOQUES';

function resolucionDe(reg) { return (reg && reg.resolucion) || {}; }

/**
 * El plan de la corrida, SOLO si es de la versión de reglas que este front entiende.
 *
 * Un plan SIN unidades no es un plan, y eso es lo mismo que decide la API: `UnidadAceptable.planDe`
 * puede derivar un plan cuyos bloques se van todos a `omitidos`, y `RegistroPropuestasService` NO lo
 * guarda —ese es el camino que deja la corrida en `LEGADO_BLOQUES`— ni lo cuenta como plan si lo
 * encontrara (`esDeEstaVersion` exige `unidades` no vacías). Tratarlo aquí como denominador cero
 * haría que el informe y el estado de la corrida que calcula la API dijeran cosas distintas de la
 * misma pantalla, que es justo lo que este módulo existe para que no pase.
 */
function plan(reg) {
    const p = resolucionDe(reg).planAceptacion;
    if (!p || p.version !== UnidadAceptable.VERSION) return null;
    return (p.unidades && p.unidades.length) ? p : null;
}

/** Las unidades del plan; lista vacía si la corrida no tiene plan. */
function unidades(reg) {
    const p = plan(reg);
    return p ? p.unidades : [];
}

/** Los bloques que el plan deja fuera (no se convierten en alta), con su motivo. */
function omitidos(reg) {
    const p = plan(reg);
    return (p && p.omitidos) || [];
}

/** Identidades de unidad acreditadas CON alta persistida, acotadas a las del plan de la corrida. */
function acreditadas(reg) {
    const p = plan(reg);
    const suyas = {};
    (p ? p.unidades : []).forEach(function (u) { if (u && u.unidadId) suyas[u.unidadId] = true; });
    const out = {};
    (resolucionDe(reg).unidadesAcreditadas || []).forEach(function (a) {
        if (a && a.unidadId && a.idPlanificacion && suyas[a.unidadId]) out[a.unidadId] = true;
    });
    return out;
}

/**
 * Identidades de unidad DESCARTADAS a mano, acotadas a las del plan y a las que no acabaron
 * creándose: un descarte de algo que después se creó no cuenta, porque el alta existe y lo que
 * existe manda sobre lo que se dijo antes de crearlo.
 */
function descartadas(reg) {
    const p = plan(reg);
    const suyas = {};
    (p ? p.unidades : []).forEach(function (u) { if (u && u.unidadId) suyas[u.unidadId] = true; });
    const hechas = acreditadas(reg);
    const out = {};
    (resolucionDe(reg).unidadesDescartadas || []).forEach(function (d) {
        if (d && d.unidadId && suyas[d.unidadId] && !hechas[d.unidadId]) out[d.unidadId] = d;
    });
    return out;
}

/**
 * Los MOTIVOS distintos por los que se descartó algo de esta corrida, en el orden en que se
 * anotaron. El motivo está guardado en cada descarte (`resolucion.unidadesDescartadas[].motivo`) y
 * sin él un «1 descartada a mano» no dice nada: quien lo lee tiene que ir al registro a saber qué
 * pasó con ese trabajo, que es justo lo que el informe existe para no obligar a hacer.
 */
function motivosDescartados(reg) {
    const fuera = descartadas(reg);
    const vistos = {};
    const out = [];
    Object.keys(fuera).forEach(function (id) {
        const m = fuera[id] && fuera[id].motivo;
        if (m && !vistos[m]) { vistos[m] = true; out.push(m); }
    });
    return out;
}

/** Día de negocio de una fecha, 'YYYY-MM-DD' en la zona del dispositivo; `null` si no hay fecha. */
function diaDe(v) {
    if (!v) return null;
    const f = (v instanceof Date) ? v : new Date(v);
    if (isNaN(f.getTime())) return null;
    const p2 = function (n) { return (n < 10 ? '0' : '') + n; };
    return f.getFullYear() + '-' + p2(f.getMonth() + 1) + '-' + p2(f.getDate());
}

/**
 * Las unidades cuyo alta se creó con OTRA DURACIÓN que la que propuso la corrida:
 * `{ unidadId: { propuestos, aceptados } }`. Vacío es lo normal.
 *
 * Los dos lados están guardados y son hechos, no deducciones: `planAceptacion.unidades[].minutos`
 * son los minutos que la corrida propuso para esa unidad —con los tramos ya consolidados— y
 * `resolucion.aceptadas[].minutos` los que llevaba el alta al crearse.
 *
 * ESTO NO CAMBIA NINGUNA CIFRA de la medición ni el veredicto de jornada: dirección decidió
 * (10/10/2026, opción 3) que la jornada se juzga con la agenda y las duraciones que vio el
 * planificador, así que editar una duración al aceptar no quita el veredicto. Sirve para una sola
 * cosa: que el informe pueda DECIR que la duración se editó, en vez de enseñar los minutos
 * propuestos mientras el calendario tiene otros sin que nada lo explique (prueba de dirección del
 * 16/10/2026).
 *
 * El emparejado es por `lineaId` —el mismo id del solver a los dos lados— y se desempata por día
 * cuando varias unidades lo comparten, que es el caso de la continuación multidía (una unidad por
 * línea y día). Si queda ambiguo no se dice nada: una nota que señale al trabajo equivocado es peor
 * que ninguna nota.
 */
function duracionesEditadas(reg) {
    const porLinea = {};
    unidades(reg).forEach(function (u) {
        if (!u || !u.lineaId || u.unidadId == null || u.minutos == null) return;
        if (!porLinea[u.lineaId]) porLinea[u.lineaId] = [];
        porLinea[u.lineaId].push(u);
    });
    const out = {};
    (resolucionDe(reg).aceptadas || []).forEach(function (a) {
        if (!a || !a.lineaId || a.minutos == null) return;
        const candidatas = porLinea[a.lineaId] || [];
        let u = candidatas.length === 1 ? candidatas[0] : null;
        if (!u) {
            const dia = diaDe(a.horaInicio);
            const delDia = candidatas.filter(function (c) { return !!dia && c.dia === dia; });
            if (delDia.length !== 1) return;
            u = delDia[0];
        }
        if (Number(u.minutos) === Number(a.minutos)) return;
        out[u.unidadId] = { propuestos: Number(u.minutos), aceptados: Number(a.minutos) };
    });
    return out;
}

/**
 * Las unidades del plan que siguen SIN alta y SIN descartar: lo que de verdad queda por decidir. Es
 * lo que explica la diferencia entre las dos caras, en vez de dejar un «23 / 26» sin cuadrar.
 */
function pendientes(reg) {
    const hechas = acreditadas(reg);
    const fuera = descartadas(reg);
    return unidades(reg).filter(function (u) {
        return !(u && u.unidadId && (hechas[u.unidadId] || fuera[u.unidadId]));
    });
}

/**
 * Las dos caras de «Aceptadas» y con qué se han contado. `base` sale en el resultado para que quien
 * pinte no vuelva a decidirlo: con `SIN_PLAN` no hay denominador que enseñar, y con `LEGADO_BLOQUES`
 * se está contando bloques del solver, que es otra unidad y hay que decirlo.
 */
function recuento(reg) {
    const p = plan(reg);
    if (p) {
        const ids = {};
        p.unidades.forEach(function (u) { if (u && u.unidadId) ids[u.unidadId] = true; });
        const total = Object.keys(ids).length;
        const hechas = Object.keys(acreditadas(reg)).length;
        const fuera = Object.keys(descartadas(reg)).length;
        return {
            base: BASE_PLAN, conDenominador: true, propuestos: total, aceptados: hechas,
            descartados: fuera, pendientes: total - hechas - fuera,
            completa: total > 0 && hechas === total, resuelta: total - hechas - fuera === 0
        };
    }
    const res = resolucionDe(reg);
    const conIdentidad = {};
    (res.unidadesAcreditadas || []).forEach(function (a) {
        if (a && a.unidadId) conIdentidad[a.unidadId] = true;
    });
    const nConIdentidad = Object.keys(conIdentidad).length;
    if (nConIdentidad) {
        // Se aceptó declarando unidades y el plan no está (su escritura es best-effort y nunca rompe
        // el Aceptar): el numerador se sabe, el denominador no. No se rellena con el recuento de
        // bloques, que es otra unidad y daría un cociente que no significa nada.
        return { base: BASE_SIN_PLAN, conDenominador: false, propuestos: 0,
            aceptados: nConIdentidad, descartados: 0, pendientes: 0, completa: false,
            resuelta: false };
    }
    const n = ((reg && reg.resultado && reg.resultado.totales
        ? reg.resultado.totales.nPropuestas : 0) || 0);
    const altas = (res.aceptadas || []).length;
    const completaLegado = n > 0 && altas >= n;
    return { base: BASE_LEGADO, conDenominador: true, propuestos: n, aceptados: altas,
        descartados: 0, pendientes: Math.max(n - altas, 0), completa: completaLegado,
        resuelta: completaLegado };
}

/**
 * Qué hizo el plan con cada BLOQUE DEL SOLVER: a qué unidad fue, si es un tramo consolidado y si se
 * quedó fuera (con su motivo). Es lo que permite al informe decir, fila a fila del proceso
 * deductivo, qué alta sale de ahí — SIN volver a deducir de qué tipo es cada fila, que es justo lo
 * que no puede hacer un consumidor: lo lee del plan, que es donde está escrito.
 *
 * Las dos caras —los bloques del plan y las filas de `resultado.propuestasResumen`— salen del mismo
 * bloque del solver, así que casan por `UnidadAceptable.claveBloque`.
 *
 * DEVUELVE UN LECTOR DE UN SOLO USO POR FILA, no un índice, y el motivo es que la clave de un
 * bloque PUEDE REPETIRSE: dos bloques idénticos —misma línea, mismo pedido, mismo minuto— tienen la
 * misma `claveBloque`, y el Aceptar hace con ellos cosas distintas (el primero crea el alta, el
 * segundo se omite por duplicado). Con un índice, el último en escribirse tapaba al anterior: el
 * omitido borraba al bloque que sí creaba el alta, las dos filas se leían como omitidas y la
 * cabecera del día decía 0 trabajos mientras el plan y el KPI contaban 1 (P2 de Codex del
 * 15/10/2026). Cada fila CONSUME una de las suertes de su clave, en el orden en que el plan las
 * declara —primero las que van a una unidad, después las omitidas—, así que con n bloques iguales se
 * leen las n suertes, una por fila, y las dos caras siguen cuadrando.
 *
 * Si las filas superan a las suertes del plan —una corrida cuyo plan se derivó de un resumen
 * distinto del que se está pintando— la fila de más repite la ÚLTIMA suerte de su clave en vez de
 * quedarse sin ninguna: el plan reconoce esa forma de bloque, y tratarla como «bloque que el plan no
 * recoge» sumaría un trabajo fantasma al día que el KPI no tiene.
 */
function porBloque(reg) {
    const colas = {};
    const ultimo = {};
    const mete = function (clave, suerte) {
        if (!colas[clave]) colas[clave] = [];
        colas[clave].push(suerte);
        ultimo[clave] = suerte;
    };
    unidades(reg).forEach(function (u) {
        const bloques = u.bloques || [];
        bloques.forEach(function (b, i) {
            mete(UnidadAceptable.claveBloque(b), {
                unidadId: u.unidadId, unidad: u, consolidado: !!b.consolidado, omitido: false,
                motivo: null, indice: i + 1, total: bloques.length
            });
        });
    });
    omitidos(reg).forEach(function (b) {
        mete(UnidadAceptable.claveBloque(b), {
            unidadId: b.unidadId || null, unidad: null, consolidado: false, omitido: true,
            motivo: b.motivo || null, indice: null, total: null
        });
    });
    return {
        /** La suerte de ESTA fila del resumen; `null` si el plan no recoge su bloque. */
        tomar: function (b) {
            const clave = UnidadAceptable.claveBloque(b);
            const cola = colas[clave];
            if (cola && cola.length) return cola.shift();
            return ultimo[clave] || null;
        }
    };
}

/**
 * Identidad de la LÍNEA DE PEDIDO de un bloque, sin el día. Es la unidad de lo que se cobra: una
 * línea se factura una vez por mucho que el planificador la parta en tramos o la reparta en varios
 * días, así que el importe de la corrida se suma por esta clave y no por bloque. `null` cuando el
 * bloque no es de una línea (los informes de oficina no llevan pedido y no tienen importe aquí).
 */
function claveLinea(p) {
    if (p && p.numeroPedido != null && p.numLinea != null) return p.numeroPedido + '#' + p.numLinea;
    return null;
}

export default {
    BASE_PLAN, BASE_SIN_PLAN, BASE_LEGADO,
    plan, unidades, omitidos, acreditadas, descartadas, motivosDescartados, duracionesEditadas,
    pendientes, recuento, porBloque, claveLinea
};
export {
    BASE_PLAN, BASE_SIN_PLAN, BASE_LEGADO,
    plan, unidades, omitidos, acreditadas, descartadas, motivosDescartados, duracionesEditadas,
    pendientes, recuento, porBloque, claveLinea
};
